import { useRef } from "react";
import type { RefObject } from "react";

import { clampSplit, DEFAULT_SPLIT } from "@/lib/slide-split";
import { cn } from "@/lib/utils";

// The draggable divider between two content windows (§4.7). It sits in the flex row/col
// between the windows and reports the pointer's position as a fraction of the container.
//
// Scale-safe: the slide canvas is rendered under a CSS `scale()` transform, but both
// `getBoundingClientRect()` and `clientX/Y` are post-transform viewport coordinates, so
// their ratio is scale-invariant — no need to know the scale factor here.
//
// Dragging calls `onPreview` (parent keeps a live local ratio, no store writes); release
// calls `onCommit` once so the whole deck is persisted a single time per drag. Double-
// click resets to an even split.
export function SplitResizer({
  orientation,
  containerRef,
  disabled,
  onPreview,
  onCommit,
}: {
  orientation: "h" | "v"; // h = windows side by side (drag horizontally); v = stacked
  containerRef: RefObject<HTMLDivElement | null>;
  disabled: boolean;
  onPreview: (ratio: number) => void;
  onCommit: (ratio: number) => void;
}) {
  const dragging = useRef(false);
  const latest = useRef(DEFAULT_SPLIT);

  const ratioAt = (e: React.PointerEvent) => {
    const el = containerRef.current;
    if (!el) return null;
    const rect = el.getBoundingClientRect();
    const r =
      orientation === "h"
        ? (e.clientX - rect.left) / rect.width
        : (e.clientY - rect.top) / rect.height;
    return clampSplit(r);
  };

  const onPointerDown = (e: React.PointerEvent) => {
    if (disabled) return;
    const r = ratioAt(e);
    if (r == null) return;
    dragging.current = true;
    latest.current = r;
    e.currentTarget.setPointerCapture(e.pointerId);
    e.preventDefault();
  };

  const onPointerMove = (e: React.PointerEvent) => {
    if (!dragging.current) return;
    const r = ratioAt(e);
    if (r == null) return;
    latest.current = r;
    onPreview(r);
  };

  const endDrag = (e: React.PointerEvent) => {
    if (!dragging.current) return;
    dragging.current = false;
    e.currentTarget.releasePointerCapture?.(e.pointerId);
    onCommit(latest.current);
  };

  const isH = orientation === "h";

  return (
    <div
      role="separator"
      aria-orientation={isH ? "vertical" : "horizontal"}
      aria-label="Resize content windows"
      onPointerDown={onPointerDown}
      onPointerMove={onPointerMove}
      onPointerUp={endDrag}
      onPointerCancel={endDrag}
      onDoubleClick={disabled ? undefined : () => onCommit(DEFAULT_SPLIT)}
      className={cn(
        "group/resizer relative flex shrink-0 items-center justify-center",
        isH ? "w-3 flex-col" : "h-3 flex-row",
        disabled
          ? "cursor-default"
          : isH
            ? "cursor-col-resize"
            : "cursor-row-resize",
      )}
    >
      {/* The grip: a slim bar, thicker/darker on hover so the handle is discoverable. */}
      <span
        className={cn(
          "rounded-full bg-slate-300 transition-colors",
          !disabled && "group-hover/resizer:bg-slate-500",
          isH ? "h-10 w-1" : "h-1 w-10",
        )}
      />
    </div>
  );
}
